/**
 * Persistent draft autosave for the chat submission box.
 *
 * Unlike {@link ./pending-chat-submission}, which hands a submission off across a
 * navigation and is consumed exactly once, a chat draft is the in-progress text a
 * user is typing but has not sent. We persist it to `sessionStorage` (which
 * survives a same-tab reload — including the auto-reload triggered when a new app
 * version deploys) so the draft is restored where the user left off.
 */

/** Rich-text editor state, mirroring the shape used by pending-chat-submission. */
type ChatDraftEditorState = { type: 'doc'; content?: unknown[] };

export type ChatDraft = {
  text?: string;
  editorState?: ChatDraftEditorState;
};

/**
 * Stable draft key for the "new chat" composer, which has no real conversationId
 * until the first message is sent (that creates the chat and navigates away).
 * A real conversationId is a UUID, so this sentinel never collides with one.
 */
export const NEW_CHAT_DRAFT_ID = 'new-chat';

const getChatDraftKey = (conversationId: string) =>
  `chat-draft-${conversationId}`;

const isMeaningfulEditorState = (
  value: unknown
): value is ChatDraftEditorState =>
  value !== undefined &&
  value !== null &&
  typeof value === 'object' &&
  (value as { type?: unknown }).type === 'doc';

/**
 * Persist the current draft. If the text is empty/whitespace we treat the draft
 * as gone and remove it rather than storing an empty entry, so a cleared box
 * never resurrects a stale draft after a reload.
 */
export const setChatDraft = (
  conversationId: string,
  draft: ChatDraft
): void => {
  const hasText =
    typeof draft.text === 'string' && draft.text.trim().length > 0;
  if (!hasText) {
    removeChatDraft(conversationId);
    return;
  }
  try {
    sessionStorage.setItem(
      getChatDraftKey(conversationId),
      JSON.stringify({
        text: draft.text,
        ...(isMeaningfulEditorState(draft.editorState) && {
          editorState: draft.editorState,
        }),
      })
    );
  } catch {
    // Storage can throw on quota-exceeded or in Safari private mode. This runs on
    // every keystroke, so a failed save must degrade to "no draft" — never break
    // typing.
  }
};

/**
 * Read the draft without removing it. Safe to call during render (no side
 * effects).
 */
export const peekChatDraft = (conversationId: string): ChatDraft | null => {
  try {
    const raw = sessionStorage.getItem(getChatDraftKey(conversationId));
    if (raw === null) {
      return null;
    }
    const parsed = JSON.parse(raw) as ChatDraft;
    const hasText =
      typeof parsed.text === 'string' && parsed.text.trim().length > 0;
    if (!hasText) {
      return null;
    }
    return {
      text: parsed.text,
      ...(isMeaningfulEditorState(parsed.editorState) && {
        editorState: parsed.editorState,
      }),
    };
  } catch {
    // Unreadable storage or corrupted JSON — treat as no draft.
    return null;
  }
};

/** Remove the stored draft. Idempotent. */
export const removeChatDraft = (conversationId: string): void => {
  try {
    sessionStorage.removeItem(getChatDraftKey(conversationId));
  } catch {
    // Storage unavailable (e.g. Safari private mode) — nothing to clean up.
  }
};
